import { MUTE_STORAGE_KEY } from './wheelConfig';

type AudioCtx = AudioContext;
type ClipName = 'whoosh' | 'menu' | 'wheel' | 'win';

const CLIP_URLS: Record<ClipName, string> = {
  whoosh: '/lucky-wheel/audio/whoosh.wav',
  menu: '/lucky-wheel/audio/menu.wav',
  wheel: '/lucky-wheel/audio/wheel.wav',
  win: '/lucky-wheel/audio/win.wav',
};

const CLIP_GAIN: Record<ClipName, number> = {
  whoosh: 0.9,
  menu: 0.55,
  wheel: 0.72,
  win: 0.8,
};

const MASTER_GAIN = 0.62;

function createContext(): AudioCtx | null {
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  return new Ctor();
}

function now(ctx: AudioCtx): number {
  return ctx.currentTime;
}

interface Voice {
  name: ClipName;
  source: AudioBufferSourceNode;
  gain: GainNode;
}

export class WheelAudio {
  private ctx: AudioCtx | null = null;
  private master: GainNode | null = null;
  private buffers = new Map<ClipName, AudioBuffer>();
  private loadPromise: Promise<void> | null = null;
  private voices = new Set<Voice>();
  muted: boolean;

  constructor() {
    this.muted = localStorage.getItem(MUTE_STORAGE_KEY) === '1';
  }

  setMuted(next: boolean): void {
    this.muted = next;
    localStorage.setItem(MUTE_STORAGE_KEY, next ? '1' : '0');
    if (this.master) this.master.gain.value = next ? 0 : MASTER_GAIN;
    if (next) this.stopAll(0.08);
  }

  async unlock(): Promise<void> {
    if (!this.ctx) {
      this.ctx = createContext();
      if (!this.ctx) return;
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : MASTER_GAIN;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume();
      } catch {
        // Mobile browsers may still block; ignore.
      }
    }
    await this.ensureLoaded();
  }

  open(): void {
    if (this.muted) return;
    void this.unlock().then(() => {
      this.playClip('whoosh');
      this.playClip('menu');
    });
  }

  closeFx(): void {
    this.stopClip('menu', 0.12);
    this.stopClip('wheel', 0.12);
    this.stopClip('win', 0.08);
    if (this.muted) return;
    void this.unlock().then(() => this.playClip('whoosh'));
  }

  click(): void {
    if (this.muted) return;
    void this.unlock().then(() => this.playClip('whoosh'));
  }

  startSpin(): void {
    if (this.muted) return;
    void this.unlock().then(() => {
      this.stopClip('menu', 0.16);
      this.stopClip('win', 0.05);
      this.playClip('wheel');
    });
  }

  setSpinProgress(progress: number): void {
    if (!this.ctx || this.muted) return;
    const p = Math.min(1, Math.max(0, progress));
    const t = now(this.ctx);
    for (const voice of this.voices) {
      if (voice.name !== 'wheel') continue;
      voice.gain.gain.setTargetAtTime(Math.max(0.0001, CLIP_GAIN.wheel * (1 - p * 0.55)), t, 0.18);
    }
  }

  tick(_progress = 0.5): void {
    // Original wheel.wav already carries the rotation texture.
  }

  win(): void {
    this.stopClip('wheel', 0.12);
    this.stopClip('menu', 0.08);
    if (this.muted) return;
    void this.unlock().then(() => this.playClip('win'));
  }

  stop(): void {
    this.stopClip('wheel', 0.18);
  }

  dispose(): void {
    this.stopAll(0.06);
    if (this.ctx) {
      void this.ctx.close().catch(() => undefined);
    }
    this.ctx = null;
    this.master = null;
    this.buffers.clear();
    this.loadPromise = null;
  }

  private async ensureLoaded(): Promise<void> {
    if (this.buffers.size === 4) return;
    if (!this.ctx) return;
    if (!this.loadPromise) {
      const ctx = this.ctx;
      this.loadPromise = Promise.all(
        (Object.keys(CLIP_URLS) as ClipName[]).map(async (name) => {
          if (this.buffers.has(name)) return;
          const response = await fetch(CLIP_URLS[name], { cache: 'force-cache' });
          if (!response.ok) throw new Error(`wheel_audio_${name}_${response.status}`);
          const bytes = await response.arrayBuffer();
          const buffer = await ctx.decodeAudioData(bytes.slice(0));
          this.buffers.set(name, buffer);
        }),
      )
        .then(() => undefined)
        .catch(() => {
          this.loadPromise = null;
        });
    }
    await this.loadPromise;
  }

  private playClip(name: ClipName): void {
    if (!this.ctx || !this.master || this.muted) return;
    const buffer = this.buffers.get(name);
    if (!buffer) return;
    if (name === 'wheel' || name === 'menu' || name === 'win') this.stopClip(name, 0.04);
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    const gain = this.ctx.createGain();
    const t = now(this.ctx);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(CLIP_GAIN[name], t + 0.03);
    source.connect(gain);
    gain.connect(this.master);
    const voice: Voice = { name, source, gain };
    this.voices.add(voice);
    source.onended = () => {
      this.voices.delete(voice);
    };
    source.start(t);
  }

  private stopClip(name: ClipName, fade = 0.12): void {
    if (!this.ctx) {
      this.voices.clear();
      return;
    }
    const t = now(this.ctx);
    for (const voice of [...this.voices]) {
      if (voice.name !== name) continue;
      try {
        voice.gain.gain.cancelScheduledValues(t);
        voice.gain.gain.setValueAtTime(Math.max(0.0001, voice.gain.gain.value), t);
        voice.gain.gain.exponentialRampToValueAtTime(0.0001, t + fade);
        voice.source.stop(t + fade + 0.02);
      } catch {
        try {
          voice.source.stop();
        } catch {
          // already stopped
        }
      }
      this.voices.delete(voice);
    }
  }

  private stopAll(fade = 0.08): void {
    for (const name of Object.keys(CLIP_URLS) as ClipName[]) this.stopClip(name, fade);
  }
}
